import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type PropsWithChildren,
} from 'react';

export interface AgentPanelContextData {
  isOpen: boolean;
  /** Whether the panel was opened from a modal (affects layout shift behavior) */
  isOpenedFromModal: boolean;
  /** Whether a page-level chat is currently registered (global chat should not render panel) */
  hasPageLevelChat: boolean;
  open: (fromModal?: boolean) => void;
  close: () => void;
  toggle: (fromModal?: boolean) => void;
  /** Register a page-level chat (call on mount) */
  registerPageChat: () => void;
  /** Unregister a page-level chat (call on unmount) */
  unregisterPageChat: () => void;
}

const AgentPanelContext = createContext<AgentPanelContextData | undefined>(
  undefined,
);

export function AgentPanelProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState({
    isOpen: false,
    isOpenedFromModal: false,
  });
  const [pageChatCount, setPageChatCount] = useState(0);

  const open = useCallback((fromModal = false) => {
    setState({ isOpen: true, isOpenedFromModal: fromModal });
  }, []);

  const close = useCallback(() => {
    setState({ isOpen: false, isOpenedFromModal: false });
  }, []);

  const toggle = useCallback((fromModal = false) => {
    setState((prev) => ({
      isOpen: !prev.isOpen,
      isOpenedFromModal: !prev.isOpen ? fromModal : false,
    }));
  }, []);

  const registerPageChat = useCallback(() => {
    setPageChatCount((c) => c + 1);
  }, []);

  const unregisterPageChat = useCallback(() => {
    setPageChatCount((c) => Math.max(0, c - 1));
  }, []);

  // Global keyboard shortcut: Cmd+L (Mac) or Ctrl+L (Windows/Linux) to toggle
  // Using capture phase to intercept before browser's native address bar shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [toggle]);

  return (
    <AgentPanelContext.Provider
      value={{
        isOpen: state.isOpen,
        isOpenedFromModal: state.isOpenedFromModal,
        hasPageLevelChat: pageChatCount > 0,
        open,
        close,
        toggle,
        registerPageChat,
        unregisterPageChat,
      }}
    >
      {children}
    </AgentPanelContext.Provider>
  );
}

export function useAgentPanel() {
  const context = useContext(AgentPanelContext);
  if (context === undefined) {
    throw new Error('useAgentPanel must be used within an AgentPanelProvider');
  }
  return context;
}
